import React from "react";
import { useTranslation } from "react-i18next";
import { SubscriptionSummary } from "../../../../../types/bookingCreation";
import { atStation, zoneOfLocation } from "../../../../../util/stationTime";
import SubscriptionStatus from "./SubscriptionStatus";
import { addressLine } from "../../../../../util/locationAddress";

type SubscriptionSummaryCardProps = {
  subscriptionSummaryData: SubscriptionSummary;
};
function SubscriptionSummaryCard({
  subscriptionSummaryData,
}: SubscriptionSummaryCardProps) {
  const { t } = useTranslation();

  const buildAddressName = (address: {
    street: string;
    postalCode: string;
    city: string;
  }): string => {
    return addressLine(address);
  }

  const startDate = subscriptionSummaryData?.startDate;
  const endDate = subscriptionSummaryData?.expiryDate;

  const locationAddress = subscriptionSummaryData.commercialModel.location.address;
  // The subscription location's clock (#3219).
  const timeZone = zoneOfLocation(subscriptionSummaryData.commercialModel.location);

  const textBoldClass =
    "text-foreground/80 font-bold leading-[20px] tracking-[0.02em] break-words";

  return (
    <div className="bg-background flex flex-col pt-8 pb-8 px-5 border border-foreground/10 relative min-h-[330px]">
      <div className="absolute -right-px top-[29%] h-[14px] w-[7px] rounded-l-full border border-r-0 border-foreground/10 bg-white" />
      <div className="absolute -left-px top-[29%] h-[14px] w-[7px] rounded-r-full border border-l-0 border-foreground/10 bg-white" />

      <div className="flex flex-row justify-end text-body2">
        <SubscriptionStatus status={subscriptionSummaryData.status} />
      </div>

      <div className="mt-1">
        <p>{t("subscriptions.ticket_overview.station")}</p>
        <p className={textBoldClass}>{buildAddressName(locationAddress) ?? "--"}</p>
        
      </div>
      
      <div className="mt-1">
        <p>{t("subscriptions.ticket_overview.service")}</p>
        <p className={textBoldClass}>
          kolula KEY - {t("subscriptions.ticket_overview.locker")} {subscriptionSummaryData?.doormappings?.[0]?.door ?? "--"}
        </p>
      </div>

      <div className="flex justify-between mt-2">
        <div className="text-left">
            <p>{t("subscriptions.ticket_overview.start")}</p>
            <p className={textBoldClass}>
            {startDate ? atStation(startDate, timeZone).format("L") : "--"}
            </p>
        </div>
        <div className="text-right">
            <p>{t("subscriptions.ticket_overview.subscription")}</p>
            <p className={textBoldClass}>
                {t(`subscriptions.recurrence.${subscriptionSummaryData?.commercialModel.recurrence}`)}
            </p>
        </div>
      </div>

      <div className="mt-1">
        <p>{subscriptionSummaryData?.cancelAtPeriodEnd 
          ? t("subscriptions.ticket_overview.cancels_on") 
          : t("subscriptions.ticket_overview.next_payment")
        }</p>
        <p className={textBoldClass}>
            {endDate ? atStation(endDate, timeZone).format("L") : "--"}
        </p>
      </div>

      <div className="mt-1">
        <p>{t("subscriptions.ticket_overview.price")}</p>
        <p className={textBoldClass}>
          {subscriptionSummaryData?.commercialModel.price}
          {" € "}
          {subscriptionSummaryData?.commercialModel.recurrence === "monthly"
            ? t("subscriptions.ticket_overview.per_month")
            : subscriptionSummaryData?.commercialModel.recurrence === "yearly"
            ? t("subscriptions.ticket_overview.per_year")
            : ""}
        </p>
      </div>

      <div className="mt-1">
        <p className="!text-base">
          {t("subscriptions.ticket_overview.subscription_number")}
        </p>
        <p className={textBoldClass}>{subscriptionSummaryData.customerAgreementNumber}</p>
      </div>
    </div>
  );
}

export default SubscriptionSummaryCard;
